import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRecordAttempt } from '../queries';
import type { Assessment } from '../types';

interface RecordAttemptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessment: Assessment | null;
}

export function RecordAttemptDialog({ open, onOpenChange, assessment }: RecordAttemptDialogProps) {
  const record = useRecordAttempt();
  const [score, setScore] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset whenever a different assessment is opened — keyed on its id (render-
  // phase pattern, no useEffect for setState).
  const activeId = open && assessment ? assessment.id : null;
  const [trackedId, setTrackedId] = useState<number | null>(null);
  if (activeId !== trackedId) {
    setTrackedId(activeId);
    setScore('');
    setError(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!assessment) return;
    const num = Number(score);
    if (Number.isNaN(num) || num < 0 || num > assessment.max_score) {
      setError(`Score must be between 0 and ${assessment.max_score}.`);
      return;
    }
    await record.mutateAsync({ assessment: assessment.id, score: num });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record attempt</DialogTitle>
          <DialogDescription>
            {assessment ? `“${assessment.title}” — passing ${assessment.passing_score}/${assessment.max_score}` : ''}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="attempt-score">Score</Label>
            <Input
              id="attempt-score"
              type="number"
              min="0"
              max={assessment?.max_score}
              value={score}
              onChange={(e) => setScore(e.target.value)}
              required
            />
            {error ? <p className="text-destructive text-sm">{error}</p> : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={record.isPending || !score.trim()}>
              {record.isPending ? 'Saving…' : 'Record attempt'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
